import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, TrendingUp, Sparkles, Target, MessageSquare, Wallet, ArrowRight, Star } from "lucide-react";
import AnimatedLogo from "@/components/AnimatedLogo";

export default function Landing() {
  return (
    <div className="min-h-screen">
      {/* Nav */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[rgba(10,11,14,0.7)] border-b border-[rgba(212,175,55,0.1)]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" data-testid="landing-nav-logo" className="flex items-center gap-2.5 group">
            <AnimatedLogo size={40} className="shrink-0" />
            <span className="font-heading text-xl font-bold gold-gradient-text tracking-tight">AfroRichMind</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-[color:var(--text-secondary)]">
            <a href="#features" className="hover:text-[color:var(--gold)] transition">Features</a>
            <a href="#pricing" className="hover:text-[color:var(--gold)] transition">Pricing</a>
            <a href="#testimonials" className="hover:text-[color:var(--gold)] transition">Reviews</a>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="landing-signin" className="text-sm text-[color:var(--text-secondary)] hover:text-[color:var(--gold)] transition">Sign in</Link>
            <Link to="/login" data-testid="landing-hero-start-button" className="gold-btn px-5 py-2.5 rounded-full text-sm">Start free</Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 pt-24 pb-16 grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[rgba(212,175,55,0.08)] border border-[rgba(212,175,55,0.2)] text-xs uppercase tracking-widest text-[color:var(--gold)] mb-6">
            <Sparkles className="w-3 h-3" /> Your AI Financial Coach
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.6 }}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] mb-6">
            Personalized to your <span className="gold-gradient-text">actual money.</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 0.6 }}
            className="text-lg text-[color:var(--text-secondary)] mb-8 max-w-xl leading-relaxed">
            Connect your accounts. Get a Financial Health Score, weekly check-ins, and one clear next move. Built for people who earn well but never feel financially organized.
          </motion.p>
          <div className="flex flex-wrap gap-4">
            <Link to="/login" data-testid="landing-hero-cta" className="gold-btn px-8 py-4 rounded-full font-semibold inline-flex items-center gap-2">
              Start 7-day free trial <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="#pricing" className="px-8 py-4 rounded-full border border-[rgba(212,175,55,0.25)] hover:border-[rgba(212,175,55,0.5)] text-sm font-medium transition">See pricing</a>
          </div>
          <div className="mt-10 flex items-center gap-6 text-xs text-[color:var(--text-muted)]">
            <div className="flex items-center gap-2"><Check className="w-4 h-4 text-[color:var(--emerald)]" /> Bank-grade security</div>
            <div className="flex items-center gap-2"><Check className="w-4 h-4 text-[color:var(--emerald)]" /> Card required · cancel anytime</div>
          </div>
        </div>

        {/* Score card visual */}
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.3, duration: 0.6 }}
          className="relative">
          <div className="glass-card p-8 pulse-glow">
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="text-xs uppercase tracking-widest text-[color:var(--text-muted)] mb-2">Financial Health</div>
                <div className="font-mono text-6xl font-bold gold-gradient-text">68<span className="text-2xl text-[color:var(--text-muted)]">/100</span></div>
              </div>
              <div className="px-3 py-1 rounded-full bg-[rgba(233,196,106,0.1)] text-[color:var(--amber)] text-xs font-medium">Fair · Growth runway</div>
            </div>
            <div className="space-y-3">
              {[
                { name: "Cash Flow", score: 82, color: "#2A9D8F" },
                { name: "Emergency Fund", score: 45, color: "#E9C46A" },
                { name: "Debt Ratio", score: 58, color: "#E06A3B" },
                { name: "Spending", score: 71, color: "#E9C46A" },
                { name: "Investing", score: 84, color: "#2A9D8F" },
              ].map((s) => (
                <div key={s.name} className="flex items-center gap-4">
                  <div className="w-32 text-sm text-[color:var(--text-secondary)]">{s.name}</div>
                  <div className="flex-1 h-2 rounded-full bg-[rgba(255,255,255,0.05)] overflow-hidden">
                    <motion.div initial={{ width: 0 }} animate={{ width: `${s.score}%` }} transition={{ delay: 0.6, duration: 1 }}
                      className="h-full rounded-full" style={{ background: s.color }} />
                  </div>
                  <div className="font-mono text-sm font-semibold w-10 text-right">{s.score}</div>
                </div>
              ))}
            </div>
          </div>
          {/* Next Best Move preview */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.6 }}
            className="glass-card p-6 mt-6 border-[rgba(224,106,59,0.3)]">
            <div className="text-xs uppercase tracking-widest text-[color:var(--terracotta)] mb-2">Your next best move</div>
            <div className="font-heading text-xl font-semibold mb-2">Pay $350 toward your Chase card</div>
            <div className="text-sm text-[color:var(--text-secondary)]">Saves ~$87/yr in interest · Emergency fund stays safe</div>
          </motion.div>
        </motion.div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-7xl mx-auto px-6 py-24">
        <h2 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight mb-3 text-center">A coach, not a dashboard.</h2>
        <p className="text-[color:var(--text-secondary)] text-center max-w-2xl mx-auto mb-16">Every week, AfroRichMind proactively tells you what's changed, what's safe, and exactly what to do next.</p>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { icon: Wallet, title: "Connect everything", desc: "Bank, credit, loans, investments. One picture of your money." },
            { icon: TrendingUp, title: "Health Score", desc: "68/100 breakdown across cash flow, debt, emergency fund, investing." },
            { icon: MessageSquare, title: "Ask anything", desc: "\"Can I afford a $700/month car?\" — get answers grounded in your real numbers." },
            { icon: Target, title: "Goals that adapt", desc: "Emergency fund, debt, house, retirement — the plan adjusts weekly." },
            { icon: Sparkles, title: "Weekly check-ins", desc: "\"You spent 23% more on restaurants this week.\" Signal, not noise." },
            { icon: Check, title: "Next Best Move", desc: "One clear action per week, backed by math. Tap to do it." },
          ].map(({ icon: Icon, title, desc }) => (
            <div key={title} className="glass-card p-6 hover:translate-y-[-2px] transition-transform">
              <div className="w-11 h-11 rounded-xl bg-[rgba(212,175,55,0.1)] flex items-center justify-center mb-4">
                <Icon className="w-5 h-5 text-[color:var(--gold)]" />
              </div>
              <h3 className="font-heading text-lg font-semibold mb-2">{title}</h3>
              <p className="text-sm text-[color:var(--text-secondary)] leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="max-w-5xl mx-auto px-6 py-24">
        <h2 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight mb-3 text-center">Simple pricing.</h2>
        <p className="text-[color:var(--text-secondary)] text-center mb-12">7 days free · card required · auto-charges after trial · cancel anytime.</p>
        <div className="grid md:grid-cols-2 gap-6">
          <div data-testid="landing-pricing-tier-premium" className="glass-card p-8">
            <div className="text-xs uppercase tracking-widest text-[color:var(--text-muted)] mb-2">Premium</div>
            <div className="mb-6">
              <span className="font-mono text-5xl font-bold">$12.99</span>
              <span className="text-[color:var(--text-secondary)]">/mo</span>
            </div>
            <ul className="space-y-3 mb-8 text-sm">
              {["Unlimited AI coaching", "Personalized financial plan", "Goals & debt optimization", "Weekly check-ins", "Spending coaching"].map(x => (
                <li key={x} className="flex items-start gap-2"><Check className="w-4 h-4 text-[color:var(--emerald)] mt-0.5 shrink-0" /> {x}</li>
              ))}
            </ul>
            <Link to="/login" className="block w-full text-center py-3 rounded-full border border-[rgba(212,175,55,0.3)] hover:border-[rgba(212,175,55,0.6)] transition">Start 7-day free trial</Link>
          </div>
          <div data-testid="landing-pricing-tier-premium-plus" className="glass-card p-8 border-[rgba(212,175,55,0.4)] relative">
            <div className="absolute -top-3 left-8 px-3 py-1 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#B38728] text-black text-xs font-semibold">Most popular</div>
            <div className="text-xs uppercase tracking-widest text-[color:var(--gold)] mb-2">Premium+</div>
            <div className="mb-6">
              <span className="font-mono text-5xl font-bold gold-gradient-text">$29.99</span>
              <span className="text-[color:var(--text-secondary)]">/mo</span>
            </div>
            <ul className="space-y-3 mb-8 text-sm">
              {["Everything in Premium", "Advanced financial planning", "Couple & family finances", "Human coach access (monthly)", "Priority AI response"].map(x => (
                <li key={x} className="flex items-start gap-2"><Check className="w-4 h-4 text-[color:var(--emerald)] mt-0.5 shrink-0" /> {x}</li>
              ))}
            </ul>
            <Link to="/login" className="gold-btn block w-full text-center py-3 rounded-full">Start 7-day free trial</Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="max-w-7xl mx-auto px-6 py-24">
        <h2 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight mb-16 text-center">Loved by high earners who felt lost.</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { name: "Jasmine A.", role: "Product Manager · $145k", text: "I made $145k and had no idea where it went. AfroRichMind told me on day one — and gave me a plan." },
            { name: "Marcus D.", role: "Engineer · $185k", text: "The Next Best Move is the killer feature. One click, one action per week. My debt fell $8k in 6 months." },
            { name: "Zoe B.", role: "Consultant · $92k", text: "Finally financial advice that uses MY numbers. Not another generic pie chart. Worth every dollar." },
          ].map((t, i) => (
            <div key={i} className="glass-card p-6">
              <div className="flex gap-1 mb-4">{[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-[color:var(--gold)] text-[color:var(--gold)]" />)}</div>
              <p className="text-[color:var(--text-secondary)] mb-4 leading-relaxed text-sm">"{t.text}"</p>
              <div className="text-sm font-semibold">{t.name}</div>
              <div className="text-xs text-[color:var(--text-muted)]">{t.role}</div>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-[rgba(212,175,55,0.1)] mt-12">
        <div className="max-w-7xl mx-auto px-6 py-8 flex items-center justify-between text-sm text-[color:var(--text-muted)]">
          <div>© 2026 AfroRichMind · Wealth-forward, personalized to your money.</div>
          <div className="flex gap-6"><a href="#" className="hover:text-[color:var(--gold)]">Privacy</a><a href="#" className="hover:text-[color:var(--gold)]">Terms</a></div>
        </div>
      </footer>
    </div>
  );
}
